import { differenceWith, findIndex, groupBy, intersectionWith, toArray } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import {
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Input,
    Label,
    UncontrolledButtonDropdown,
    Collapse,
} from 'reactstrap';
import { bind } from '../Utils/Bind';
import { deepStringFind } from '../Utils/DeepStringFind';
import { mobxEqual } from '../Utils/MobxTools';
import { Optional } from '../Utils/Types';
import { BsCaretDownFill } from '@react-icons/all-files/bs/BsCaretDownFill';
import { BsExclamationTriangleFill } from '@react-icons/all-files/bs/BsExclamationTriangleFill';

export interface IMultiSelectOption<T> {
    option: T;
    group?: string;
}

export interface IDropdownMultiSelectFilterProps<T> extends React.Props<DropdownMultiSelectFilter<T>> {
    onChange(filter: readonly IMultiSelectOption<T>[] | null | undefined): void;
    onToggle?(isOpen: boolean): void;
    onGroupSelectAllChange?(group: string, checked: boolean): void;
    print?(entity: T): string;
    sort?(a: IMultiSelectOption<T>, b: IMultiSelectOption<T>): number;
    comparer?(a: T, b: T): boolean;
    readonly title: string | React.ReactNode;
    readonly defaultText: string;
    readonly dynamicTitle: boolean;
    readonly showActive: boolean;
    readonly showClear: boolean;
    readonly showOnlyExclusiveConflicts: boolean;
    readonly renderBefore?: React.ReactNode;
    readonly all?: readonly IMultiSelectOption<T>[];
    readonly options?: IMultiSelectOption<T>[];
    readonly filter?: readonly IMultiSelectOption<T>[] | null;
    readonly style?: React.CSSProperties;
    readonly className?: string;
    readonly nav?: boolean;
    readonly caret?: boolean;
    readonly link?: boolean;
    readonly size?: 'md' | 'sm' | 'lg';
    readonly openOnMount?: boolean;
    readonly hideConflicts?: boolean;
}
@observer
export class DropdownMultiSelectFilter<T> extends React.Component<IDropdownMultiSelectFilterProps<T>> {
    public static defaultProps: Optional<IDropdownMultiSelectFilterProps<any>> = {
        dynamicTitle: true,
        showActive: true,
        showClear: true,
    };

    public constructor(props: IDropdownMultiSelectFilterProps<T>) {
        super(props);

        this.comparer = props.comparer || mobxEqual;
    }

    public render() {
        return (
            <UncontrolledButtonDropdown
                isOpen={this.dropdownOpen}
                toggle={this.onToggle}
                className={this.props.className}
            >
                <DropdownToggle
                    nav={this.props.nav}
                    caret={this.props.caret}
                    className={this.titleStyle}
                    size={this.props.size}
                    color={this.props.link ? 'link' : undefined}
                >
                    {this.title}
                </DropdownToggle>
                <DropdownMenu className="p-0 dropdown-menu-left added-dropdown-width dropdown-height">
                    <Input
                        type="text"
                        placeholder={this.props.defaultText || `Find ${this.props.title}...`}
                        value={this.findString}
                        onChange={this.onFindChange}
                        autoFocus
                    />
                    {this.props.renderBefore}

                    {!!this.props.filter?.length && this.props.showClear && (
                        <DropdownItem className="text-center" onClick={this.clear}>
                            Clear Selected Items
                        </DropdownItem>
                    )}

                    {this.options
                        ?.filter(o => o != null)
                        .map((o, k) => {
                            return this.renderGroup(o, k);
                        })}
                    {this.allItems && <DropdownItem divider />}
                    {this.allItems && (
                        <DropdownItem header>
                            All {this.props.title} 
                        </DropdownItem>
                    )}

                    {this.allItems?.map(this.renderEntry)}

                    {this.props.children}
                </DropdownMenu>
            </UncontrolledButtonDropdown>
        );
    }

    @bind
    @action
    private renderGroup(value: IMultiSelectOption<T>[], key: any) {
        const { onGroupSelectAllChange } = this.props;
        const group = value[0].group;

        return (
            <div className="px-1" key={key}>
                {group && (
                    <MultiSelectGroupEntry
                        group={group}
                        search={this.findString}
                        renderAfterButton={
                            onGroupSelectAllChange && (
                                <div className="ms-auto">
                                    {this.getGroupCountSelected(group) > 0 && (
                                        <span className="me-2">({this.getGroupCountSelected(group)})</span>
                                    )}
                                    <Input
                                        type="checkbox"
                                        checked={this.isGroupChecked(group)}
                                        onChange={() => this.onGroupSelectAllChange(group)}
                                    />
                                </div>
                            )
                        }
                    >
                        {value.filter(o => o != null).map(this.renderEntry)}
                    </MultiSelectGroupEntry>
                )}

                {!group && value.filter(o => o != null).map(this.renderEntry)}

                {group && <hr className="my-1" />}
            </div>
        );
    }

    @bind
    @action
    private renderEntry(entry: IMultiSelectOption<T>, key: any) {
        return (
            <div key={key} className="px-1">
                <Label check className="m-1">
                    <Input
                        type="checkbox"
                        checked={this.isChecked(entry)}
                        onChange={event => this.check(event, entry)}
                    />{' '}
                    {this.props.print ? this.props.print(entry.option) : entry}
                </Label>
            </div>
        );
    }

    @computed
    private get allItems() {
        if (!this.props.all) return;

        let items = this.props.all.slice();

        if (this.findString)
            items = items.filter(e =>
                deepStringFind(this.props.print ? this.props.print(e.option) : e, this.findString)
            );

        if (this.props.options)
            items = differenceWith(items, this.props.options, (a, b) => this.comparer(a.option, b.option));

        if (this.props.sort) items = items.sort(this.props.sort);

        return items;
    }

    @computed
    private get options() {
        if (!this.props.options) return;

        let items = this.props.options.slice();

        if (this.findString)
            items = items.filter(e =>
                deepStringFind(this.props.print ? this.props.print(e.option) : e, this.findString)
            );

        if (this.props.sort) items = items.sort(this.props.sort);
        items = items.filter(o => o != null);

        const groupedItems = toArray(groupBy(items, 'group'));

        return groupedItems;
    }

    @computed
    private get title() {
        if (this.props.dynamicTitle && this.props.filter?.length) {
            const strings = this.props.filter.map(e => (this.props.print ? this.props.print(e.option) : e));
            return strings.join(', ');
        } else {
            return this.props.title;
        }
    }

    @computed
    private get titleStyle() {
        if (this.props.filter?.length && this.props.showActive) {
            return 'border-bottom-primary ';
        }
    }

    @bind
    private isGroupChecked(group: string) {
        const filteredOptions = this.props.filter?.filter(i => i.group == group);
        const allOptions = this.props.options?.filter(i => i.group == group);
        return filteredOptions?.length == allOptions?.length ? true : false;
    }

    @bind
    private getGroupCountSelected(group: string) {
        const filteredOptions = this.props.filter?.filter(i => i.group == group);
        return filteredOptions?.length ?? 0;
    }

    @bind
    private isChecked(entity: IMultiSelectOption<T>) {
        return findIndex(this.props.filter, e => this.comparer(e.option, entity.option)) != -1;
    }

    @computed
    private get hasConflicts() {
        if (this.props.hideConflicts) return false;

        if (this.props.showOnlyExclusiveConflicts) {
            return (
                intersectionWith(this.props.filter, this.props.options ?? [], (a, b) =>
                    this.comparer(a.option, b.option)
                ).length == 0
            );
        } else {
            return (
                differenceWith(this.props.filter, this.props.options ?? [], (a, b) => this.comparer(a.option, b.option))
                    .length > 0
            );
        }
    }

    @bind
    @action
    private onToggle() {
        this.dropdownOpen = !this.dropdownOpen;
        this.findString = '';
        if (this.props.onToggle) this.props.onToggle(this.dropdownOpen);
    }

    @bind
    @action
    private onFindChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.findString = target.value;
    }

    @bind
    @action
    private clear() {
        this.props.onChange(undefined);
    }

    @bind
    @action
    private check(event: React.FormEvent<HTMLInputElement>, entity: IMultiSelectOption<T>) {
        const filter = this.props.filter ?? [];
        const target = event.target as HTMLInputElement;
        const checked = this.isChecked(entity);

        if (target.checked && !checked) {
            if (entity.group) {
                this.props.onChange(filter.concat(entity));
            } else {
                this.props.onChange(filter.concat({ option: entity.option }));
            }
        } else if (!target.checked && checked) {
            this.props.onChange(filter.filter(e => !this.comparer(e.option, entity.option)));
        }
    }

    @bind
    @action
    private onGroupSelectAllChange(group: string) {
        if (this.props.onGroupSelectAllChange) this.props.onGroupSelectAllChange(group, this.isGroupChecked(group));
    }

    private comparer: (a: T, b: T) => boolean;
    @observable
    private findString: string = '';
    @observable
    private dropdownOpen = false;
}

export interface IMultiSelectGroupEntryProps {
    readonly group: string;
    readonly renderAfterButton: React.ReactNode;
    readonly search: string;
}

@observer
export class MultiSelectGroupEntry extends React.Component<IMultiSelectGroupEntryProps> {
    public render() {
        const { group, renderAfterButton } = this.props;

        return (
            <>
                <div className="px-2 nav-item d-flex align-items-center">
                    {group}

                    {group && (
                        <button className="btn btn-link" onClick={this.toggleCollapse}>
                            <BsCaretDownFill />
                        </button>
                    )}

                    {renderAfterButton}
                </div>

                <Collapse isOpen={this.props.search.length > 0 || this.showCollapse}>{this.props.children}</Collapse>
            </>
        );
    }

    @bind
    @action
    private toggleCollapse() {
        this.showCollapse = !this.showCollapse;
    }

    @observable
    private showCollapse = false;
}
